import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import pure from 'utils/pure';
import Header from 'components/shared/header/header';
import Footer from 'components/shared/footer/footer';
import Background from 'components/shared/background/background';
import Preloader from 'components/shared/preloader/preloader';
import Transition from 'components/core/transition/transition';
import Modal from 'components/shared/modal/modal';
import * as pages from 'pages/showtimes_pages';
import Cookies from 'components/shared/cookies/cookies';
import RenderedPixels from 'components/shared/pixels/renderedPixels';
import EmbedMode from 'components/shared/embedMode/embedMode';
import 'utils/pixels';
import hoverDetection from 'utils/hoverDetection';
import s from 'base/showtimes/showtimesApp.sss';
import OrientationPrompt from 'components/shared/orientationPrompt/orientationPrompt';
import ScrollPage from 'pages/showtimes_features';
import SocialShare from 'components/shared/socialShare/socialShare';
import { getPageUrl } from 'components/core/link/link';

// import 'preact/devtools' // Breaks IE10 , so test again before enabling
import { accessNested, joinClasses } from 'utils/utils';
import { getPath } from 'utils/clientUtils';
import IEMessage from 'base/showtimes/IEMessage';

const isServer = typeof window === 'undefined';

import { marked } from 'marked';
// Custom markdown renderer
const markedRenderer = new marked.Renderer();
// Allow links with a leading + to open in a new tab e.g. [text](+http://test.com)
// also advanced page linking like [text](page:videos?v=1#top)
markedRenderer.link = ({ href, title, text }) => {
	let attributes = {};
	if (title) attributes.title = title;
	if (href && href[0] === '+') {
		href = href.slice(1);
		attributes.target = '_blank';
	} else if (href && href.startsWith('page:')) {
		let data = href.match(/^page:([^?#]*)(\?.*)?(#.*)?$/) || [href];
		let safeString = str => "'" + str.replace(/'/g, "\\'") + "'";
		let pageId = data[1];
		let qs = data[2] ? data[2].slice(1) : '';
		let hash = data[3] ? data[3].slice(1) : '';
		href = getPageUrl(pageId, qs, false, hash) || '#';
		attributes.onclick = 'event.preventDefault();goToPage(' + safeString(pageId) + ', ' + safeString(qs) + ', false, ' + safeString(hash) + ')';
	}
	try {
		href = encodeURI(href).replace(/%25/g, '%');
	} catch (e) {
		return text;
	}
	// Not sure I remember why we use escape here - maybe we can remove ?
	attributes.href = escape(href).replace(/%3A/g, ':');
	let attrString = Object.keys(attributes).map(key => key + '="' + attributes[key] + '"').join(' ');
	return '<a ' + attrString + '>' + text + '</a>';
};
marked.use({
	renderer: markedRenderer,
});

export default @pure class ShowtimesApp extends Component {
	constructor(props) {
		super();

		this.noInitialTransition = true;

		this.lockScroll = this.lockScroll.bind(this);
		this.doLockScroll = this.lockScroll.bind(this, true);
		this.doUnlockScroll = this.lockScroll.bind(this, false);
		this.state.appState = props.initialStore;
		Store.set(props.initialStore);

		if (!isServer) {
			Store.emit(actions.BEFORE_LOAD);
			document.body.classList.add('js-loaded');
		}

		Store.on('update', () => {
			const store = Store.get();

			let oldPage = this.state.appState.activePage;
			let oldPageId = oldPage && oldPage.id;
			// Add in the old page, so we can keep the class on the parent during the transition
			// to not break page specific styling
			if (oldPageId && oldPageId !== store.activePage.id) {
				// remove the timer if it already exists
				clearInterval(this.oldTimer);
				this.oldTimer = setTimeout(() => {
					// Remove old page again after the transition is done
					// Do we actually need that ? Could prevent an extra render if not
					this.setState({ oldPageId: '' });
				}, this.transitionDuration || 1000);
			}
			this.setState({ appState: store, oldPageId });
		});

		this.transitionDuration = accessNested(props, 'initialStore.appData.styling.variables.customScrollDuration', 1000);
	}

	componentDidMount() {
		let isIos = ['iphone', 'ipad'].indexOf(this.state.appState.client.device) !== -1;
		if (isIos) {
			document.body.addEventListener('focus', e => {
				let tagName = (e.target && e.target.tagName) || '';
				if (tagName.toLowerCase() === 'select') {
					Store.emit(actions.SET_IOSFIXEDFIX, true);
				}
			}, true);
			document.body.addEventListener('blur', () => {
				if (this.state.appState.iosFixedFix) {
					Store.emit(actions.SET_IOSFIXEDFIX, false);
				}
			}, true);
		}

		window.addEventListener('keydown', this.handleTab);
		window.addEventListener('mousedown', this.handleMouseDown);

		if (accessNested(this.state, 'appState.appData.options.disableLandscape')) {
			Store.emit(actions.START_FORCE_CLIENT_ORIENTATION, 'portrait');
		}

		Store.on(actions.SCROLL_LOCK, this.doLockScroll);
		Store.on(actions.SCROLL_UNLOCK, this.doUnlockScroll);

		let client = accessNested(this.state, 'appState.client') || {};
		if (client.isIOS && client.iframe) {
			// --- Ugly hack ---
			// Issue: iOS doesn't render things it considers off-screen
			// Some of our iframes are still considered off-screen even after they come into view
			// Changing the scroll position forces a refresh and makes things appear if the iframe is effectively in the view
			// we cannot detect when we need to trigger that refresh (no access to the containing document)
			// so we do that in a loop, until the user has interacted with the frame (which means the frame is now into view)
			//   still not ideal as only triggers after the scroll momentum stops (javascript throttled / disabled during scroll)
			this.iosIframeFixInterval = setInterval(() => {
				if (!this.base) {
					return;
				}
				let back = this.base.scrollTop;
				this.base.scrollTop = back + 1;
				this.base.scrollTop = back;
			}, 100);
			document.addEventListener('touchstart', () => clearInterval(this.iosIframeFixInterval));
		}
		hoverDetection();
	}

	componentWillUpdate(newProps, newState) {
		let prevPage = this.state.appState.activePage;
		if (prevPage !== newState.appState.activePage) {
			this.prevComp = prevPage.compName || prevPage.id;
		}
	}

	componentWillUnmount() {
		clearInterval(this.iosIframeFixInterval);
		window.removeEventListener('keydown', this.handleTab);
		window.removeEventListener('mousedown', this.handleMouseDown);
		Store.off(actions.SCROLL_LOCK, this.doLockScroll);
		Store.off(actions.SCROLL_UNLOCK, this.doUnlockScroll);
	}

	lockScroll(lock) {
		let overflow = lock ? 'hidden' : '';
		document.body.style.overflow = overflow;
		document.documentElement.style.overflow = overflow;
	}

	getExtraPageProps() { }

	getPageModule(activePage) {
		return pages[activePage.compName || activePage.id];
	}

	closeMap() { }

	handleTab(e) {
		if (e.key === 'Tab') {
			document.body.classList.add('tabbing');
		}
	}

	handleMouseDown() {
		document.body.classList.remove('tabbing');
	}

	getHeader(client, appData, appState, activePage, pageProps) {
		return (
			<Header
				client={client}
				appData={appData}
				dynamicData={appState.dynamicData}
				background={appState.background}
				showtimes={appState.showtimes}
				path={getPath(activePage)}
				headerData={appState.header}
				activePage={activePage}
				iosFixedFix={pageProps.iosFixedFix}
			/>
		);
	}

	getExtra() { }

	getCompClass(compName, verticallyCentered) {
		return joinClasses({
			s,
			full: accessNested(this.state, 'appState.header.sideNavActive'),
			verticalCenter: verticallyCentered,
		}, 'page-comp-' + compName, s['comp-' + compName]);
	}

	getSonyHeader() { }

	render(props, state) {
		let { appState } = state;

		// TODO what happens when the page doesn't exist, replace 404, or make it redirect to the home?
		let activePage = appState.activePage;
		const appData = appState.appData;
		const copy = appData.copy;
		const client = appState.client;
		const uiState = appState.header.uiState || {};
		const extraSonyHeader = appState.header && appData.header.sonyLogos;

		// Let's just redirect to any other available page for now
		if (!activePage && appData.pages) {
			activePage = appData.pages[Object.keys(appData.pages)[0]];
			console.log('No active page set, switching to ' + activePage.id);
		}
		if (!Object.prototype.hasOwnProperty.call(appData.pages, activePage.id)) {
			console.log('!!! Unavailable page:', activePage.id);
		}

		// If the user is using an old IE version, show an "upgrade" message
		const oldIE = client.isIE && client.isIE < 11;
		if (oldIE) {
			return <IEMessage appData={appData} client={client} activePage={activePage} />;
		}

		let pageData = accessNested(appState, ['appData', 'pages', activePage.id, 'data']) || {};
		let fixedFix = client.isIOS && appState.iosFixedFix;
		if (fixedFix) {
			fixedFix = document.body.scrollTop;
		}

		const { socialShareBar } = pageData;
		const { shareImage, roots } = appData.urls;

		let compName = activePage.compName || activePage.id;
		let pageModule = this.getPageModule(activePage);

		// TODO: Handle that differently ?
		if (appData.options.useScrollSite && activePage.sectionType) {
			pageModule = ScrollPage;
			// Reset id for
			pageProps.id = 'scrollSite';
		}

		let renderedPageData = accessNested(appState, ['appData', 'pages', activePage.id, 'data']) || {};
		let pageProps = {
			id: activePage.id,
			sideNavActive: appState.header.sideNavActive,
			client: appState.client,
			appData: appState.appData,
			pageData: renderedPageData, // Let's use the appData version, the data might have updated in the store, and so the activePage won't have the latest version
			activePage: activePage,
			iosFixedFix: fixedFix
		};
		let pageDeps = pageModule && pageModule.dependencies;
		if (pageDeps) {
			for (let dep in pageDeps) {
				let name = dep;
				if (typeof pageDeps[dep] === 'string') {
					name = pageDeps[dep];
				}
				pageProps[name] = appState[dep] || accessNested(this, dep);
			}
		}

		Object.assign(pageProps, this.getExtraPageProps(pageProps));

		let verticallyCentered = pageModule && !!pageModule.verticallyCenter;
		let prevModule = pages[this.prevComp];
		let prevVerticallyCentered = !!(prevModule && prevModule.verticallyCenter);

		let isBarebones = false;
		if (!isServer) {
			isBarebones = location.search.indexOf('showbarebones') !== -1;
			if (window.showbarebones !== undefined) {
				isBarebones = window.showbarebones;
			}
		}

		let fullClass = joinClasses(
			s.app,
			client.device,
			client.orientation,
			activePage.compName,
			activePage.id,
			this.state.oldPageId && ('from-' + this.state.oldPageId),
			{
				barebones: isBarebones,
				isTouch: client.isTouch,
				isIE: client.isIE,
				isEdge: client.isEdge,
				isChrome: client.isChrome,
				isFirefox: client.isFirefox,
				isSafari: client.isSafari,
				isIOS: client.isIOS,
				iframe: client.iframe,
				videoNotPlaying: appState.background && !appState.background.videoPlaying,
				sideNav: appState.header.sideNavActive,
				deviceMapOpen: appState.showtimes && appState.showtimes.mapOpen,
				// isNotKaltura: appState.videos.isNotKaltura,
				isNotMatterhorn: appState.videos.isNotMatterhorn,
				// footer.hideFooter is being used for sony primarily
				footerHidden: appState.footer && appState.footer.hideFooter,
				extraSonyHeader: extraSonyHeader
			}
		);

		let orientationPrompt;
		if (OrientationPrompt) {
			orientationPrompt = (
				<OrientationPrompt
					client={client}
					overlayOrientation={uiState.overlayOrientation}
					app={this}
				/>
			);
		}

		let currentCompClass = this.getCompClass(compName, verticallyCentered);
		let prevCompClass = this.getCompClass(this.prevComp, prevVerticallyCentered);

		const PageComp = pageModule && pageModule.default;
		if (!PageComp) {
			console.error('THE PAGE MODULE DOES NOT EXIST');
			return;
		}
		return (
			<div
				class={fullClass}
				id="showtimesApp"
			>
				{appState.showtimes && appState.showtimes.mapOpen && this.closeMap()}
				{extraSonyHeader && this.getSonyHeader()}
				{this.getHeader(client, appData, appState, activePage, pageProps)}
				<Transition
					key="content"
					class={s.content}
					id="appContent"
					duration={this.transitionDuration || 1000}
					baseClass={s.transition}
					enteringClass={joinClasses(s.entering, currentCompClass)}
					leavingClass={joinClasses(s.leaving, prevCompClass)}
					visibleClass={joinClasses(s.visible, currentCompClass)}
					emptyClass={s.empty}
					noInitialTransition={this.noInitialTransition}
				>
					<PageComp {...pageProps} />
				</Transition>
				<Footer
					appData={appData}
					activePage={activePage}
					pageData={pageData}
					client={client}
					footerStore={appState.footer}
				/>
				<Cookies appData={appData} misc={appState.misc} />
				<Background
					appData={appData}
					client={client}
					bgState={appState.background}
					bgData={appData.background}
					roots={appState.appData.urls.roots}
					pageId={activePage.id}
					pageBgData={(pageData && pageData.background) || {}}
					iosFixedFix={fixedFix}
				/>
				<Preloader loader={appState.loader} />
				<RenderedPixels />
				<Modal {...(appState.modal || {})} />
				{orientationPrompt}
				{client.embedMode && <EmbedMode client={client} />}
				{socialShareBar && (
					<SocialShare
						class={s.socialShare}
						assetRoot={roots}
						links={socialShareBar.shareOptions}
						isMobile={client.deviceSize === 'mobile'}
						copy={copy}
						image={socialShareBar.image || shareImage}
						url={socialShareBar.url}
						shareCopy={socialShareBar.copy}
					/>
				)}
				{this.getExtra()}
			</div>
		);
	}
}
